import { MouseEvent, useCallback, useMemo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { ChevronIcon, KIcon, SaveIcon } from '@karnott/icons';
import { ActionMenuItem, ActionMenuPopover } from '@karnott/popover-menu';
import { fontFamily, pixelSpacing, size, spacing } from '@karnott/theme';
import { Tooltip } from '@karnott/tooltip';
import { ButtonMode, buttonColors } from './buttonColors';
import { Effects } from './effects';

// Create the keyframes
const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(359deg);
  }
`;

const WithAlternativesWrapper = styled.div`
  display: flex;
`;

function getBaseButtonStyle(props: {
  backgroundColor: string;
  borderColor: string;
  $color: string;
  large?: boolean;
  $loading?: boolean;
  rounded?: boolean;
  thin?: boolean;
  plain?: boolean;
  image?: string;
  disabled?: boolean;
}) {
  return css`
    position: relative;
    background-color: ${props.backgroundColor};
    color: ${props.$color};
    padding: ${props.plain ? 0 : props.thin ? `0 ${spacing('small')}` : props.large ? 10 : spacing('small')}px;
    border-radius: ${props.rounded ? spacing('large') : props.large ? 5 : spacing('xSmall')}px;
    border: ${() => {
      if (props.$loading) return '1.5px solid rgba(0,0,0,0.4)';
      if (props.borderColor) return `1.5px solid ${props.borderColor}`;
      return '1.5px solid transparent';
    }};
    transition:
      color 0.1s linear,
      background-color 0.1s linear,
      border-color 0.1s linear;
    outline: none;
    text-decoration: none;
    display: flex;
    align-items: center;
    cursor: ${props.disabled ? 'not-allowed' : 'pointer'};
    vertical-align: middle;
    gap: ${props.thin ? pixelSpacing('xSmall') : pixelSpacing('small')};
  `;
}

const Wrapper = styled.button<{
  backgroundColor: string;
  borderColor: string;
  $color: string;
  large?: boolean;
  $loading?: boolean;
  rounded?: boolean;
  thin?: boolean;
  plain?: boolean;
  image?: string;
  withAlternatives?: boolean;
}>`
  ${getBaseButtonStyle}

  font-family: ${fontFamily()};
  font-size: ${({ large }) => (large ? size('large') : size())}px;

  &.badge {
    // avoid border color glitch in button group
    transition:
      color 0.1s linear,
      background-color 0.1s linear,
      border-left-color 0.1s linear,
      border-right-color 0.1s linear;
  }

  ${({ image, large }) =>
    image &&
    css`
      box-sizing: border-box;
      background-image: url(${image});
      background-size: cover;
      background-position: center;
      width: ${large ? 43 : 34}px;
      height: ${large ? 43 : 34}px;
    `};

  ${({ $loading }) =>
    $loading &&
    css`
      color: transparent !important;
      pointer-events: none;
      position: relative;
      overflow: hidden;
      &::before {
        z-index: 100;
        background: rgba(0, 0, 0, 0.4);
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
      }
      &::after {
        z-index: 101;
        animation: ${rotate} 0.5s linear infinite;
        border-radius: 290486px;
        border-color: transparent transparent #dbdbdb #dbdbdb;
        border-style: solid;
        border-width: 2px;
        content: '';
        display: block;
        height: 1em;
        width: 1em;
        position: absolute !important;
        left: calc(50% - 0.6em);
        opacity: 1 !important;
        box-sizing: content-box;
      }
    `};

  ${({ withAlternatives }) =>
    withAlternatives
      ? css`
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        `
      : null}
`;

const AlternativesTrigger = styled.button<{
  backgroundColor: string;
  borderColor: string;
  $color: string;
  large?: boolean;
  $loading?: boolean;
  rounded?: boolean;
  thin?: boolean;
  plain?: boolean;
  image?: string;
}>`
  ${getBaseButtonStyle}

  border-left: none;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
`;

const IconContainer = styled.div<{
  $loading?: boolean;
}>`
  display: flex;
  align-items: center;
  ${({ $loading }) =>
    $loading &&
    css`
      svg * {
        stroke: transparent;
      }
    `}
`;

const Badge = styled.div<{
  background: string;
  border: string;
  numberColor: string;
  padding: boolean;
  large?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  bottom: 5px;
  left: ${({ large }) => (large ? '-8px' : '-6px')};
  background-color: ${({ background }) => background};
  box-shadow: inset 0 0 0 1.5px ${({ border }) => border};
  transition: all 0.1s linear;
  color: ${({ numberColor }) => numberColor};
  font-size: ${({ large }) => (large ? `${size('xSmall')}px` : '8px')};
  border-radius: 30px;
  font-weight: 500;
  user-select: none;
  height: ${({ large }) => (large ? '15px' : '12px')};
  min-width: ${({ large }) => (large ? '15px' : '12px')};
  padding: 0 ${({ padding }) => padding && '3px'};
  box-sizing: border-box;
`;

function getButtonColors({
  bleen,
  danger,
  disabled,
  hovered,
  outlined,
  outlinedNeutral,
  plain,
  primary,
  success,
  warning,
}: {
  bleen?: boolean;
  danger?: boolean;
  disabled?: boolean;
  hovered?: boolean;
  outlined?: boolean;
  outlinedNeutral?: boolean;
  plain?: boolean;
  primary?: boolean;
  success?: boolean;
  warning?: boolean;
}) {
  let mode: ButtonMode = 'default';
  if (primary) mode = 'primary';
  if (success) mode = 'success';
  if (danger) mode = 'danger';
  if (warning) mode = 'primary'; // for now, warning is orange as primary color
  if (bleen) mode = 'bleen';
  if (plain) mode = 'plain';
  const definition =
    buttonColors[mode][outlined ? 'outlined' : outlinedNeutral ? 'outlinedNeutral' : 'default'][
      disabled ? 'disabled' : hovered ? 'hovered' : 'default'
    ];
  return [definition.bg, definition.font, definition.border] as const;
}

type Orientation = 'top' | 'right' | 'bottom' | 'left';

type Props = {
  /** Whether the button is disabled */
  disabled?: boolean;
  /** The icon to display on the button */
  Icon?: KIcon;
  /** The image source to display as a background on the button */
  image?: string;
  /** The label of the button */
  title?: string;
  /** The function to call when the button is clicked */
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  /** The URL to link to. If not empty, the button is an HTML link tag. */
  href?: string;
  /** Whether the link should open in a new tab */
  newTab?: boolean;
  /** Whether the button has an outlined style (inverted background color) */
  outlined?: boolean;
  /** Whether the button has an outlined style with neutral colors */
  outlinedNeutral?: boolean;
  /** Whether the button is a success button */
  success?: boolean;
  /** Whether the button is a primary button */
  primary?: boolean;
  /** Whether the button is a danger button */
  danger?: boolean;
  /** Whether the button is a warning button */
  warning?: boolean;
  /** Whether the button is a bleen button */
  bleen?: boolean;
  /** Whether the button has a plain style (only text) */
  plain?: boolean;
  /** Whether the button is large */
  large?: boolean;
  /** Whether the button shows a loader */
  loading?: boolean;
  /** The message to display in a tooltip when hovering the button */
  message?: string;
  /** Whether the button has rounded corners */
  rounded?: boolean;
  /** Whether the button is thin */
  thin?: boolean;
  /** The badge to display on the button. Can be a tick or a number. */
  badge?: boolean | number;
  /** The orientation of the tooltip */
  tooltipOrientation?: Orientation;
  /** Whether the tooltip text should wrap or stay on one line */
  noWrap?: boolean;
  /** Alternative buttons accessible from an arrow on the right of the main button */
  alternatives?: ActionMenuItem[];
};

const empty: never[] = [];

/** A button to indicate a trigger for an action */
export const Button = ({
  disabled,
  Icon,
  image,
  title,
  onClick,
  href,
  newTab,
  outlined,
  outlinedNeutral,
  success,
  primary,
  danger,
  warning,
  bleen,
  plain,
  large,
  loading,
  message,
  rounded,
  thin,
  badge,
  tooltipOrientation,
  noWrap,
  alternatives = empty,
}: Props) => {
  const [hovered, onEnter, onLeave] = Effects.useButtonHover();
  const [alternativesHovered, onAlternativesEnter, onAlternativesLeave] = Effects.useButtonHover();

  const [alternativesOpened, setAlternativesOpened] = useState(false);
  const alternativesTriggerRef = useRef<HTMLButtonElement | null>(null);

  const onClickCB = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      !disabled && !loading && onClick && onClick(e);
    },
    [disabled, loading, onClick],
  );

  const colorProps = {
    bleen,
    danger,
    disabled,
    hovered,
    outlined,
    outlinedNeutral,
    plain,
    primary,
    success,
    warning,
  };

  const [backgroundColor, fontColor, borderColor] = getButtonColors(colorProps);

  const [alternativesBackgroundColor, alternativesFontColor, alternativesBorderColor] = getButtonColors({
    ...colorProps,
    hovered: alternativesHovered || alternativesOpened,
  });

  const iconSize = useMemo(() => {
    if (thin) return size('regular');
    if (large) return 20;
    return 16;
  }, [large, thin]);

  const element = useMemo(() => {
    if (href) return 'a';
    return 'button';
  }, [href]);

  return (
    <Tooltip
      message={message}
      orientation={tooltipOrientation ? tooltipOrientation : 'right'}
      noWrap={noWrap}
      unstyledContent
      content={
        <WithAlternativesWrapper>
          <Wrapper
            as={element}
            onMouseEnter={disabled ? undefined : onEnter}
            onMouseLeave={disabled ? undefined : onLeave}
            disabled={disabled}
            backgroundColor={backgroundColor}
            borderColor={borderColor}
            $color={fontColor}
            onClick={onClickCB}
            href={disabled ? undefined : href}
            target={href && newTab ? '_blank' : undefined}
            large={large}
            $loading={loading}
            rounded={rounded}
            thin={thin}
            plain={plain}
            image={image}
            className={badge ? 'badge' : undefined}
            withAlternatives={alternatives.length > 0}
          >
            {!!Icon && !image ? (
              <IconContainer $loading={loading}>
                <Icon color="currentColor" size={iconSize} />
              </IconContainer>
            ) : null}
            {!!title && !image ? title : null}
            {badge && !plain ? (
              <Badge
                border={borderColor}
                background={fontColor}
                numberColor={backgroundColor}
                padding={badge !== true}
                large={large}
              >
                {badge === true ? (
                  <SaveIcon circled backgroundColor="transparent" size={large ? 15 : 12} color={backgroundColor} />
                ) : (
                  badge
                )}
              </Badge>
            ) : null}
          </Wrapper>
          {alternatives.length > 0 ? (
            <>
              <AlternativesTrigger
                onMouseEnter={disabled ? undefined : onAlternativesEnter}
                onMouseLeave={disabled ? undefined : onAlternativesLeave}
                disabled={disabled}
                backgroundColor={alternativesBackgroundColor}
                borderColor={alternativesBorderColor}
                $color={alternativesFontColor}
                large={large}
                rounded={rounded}
                thin={thin}
                plain={plain}
                onClick={() => setAlternativesOpened(!alternativesOpened)}
                ref={alternativesTriggerRef}
                data-testid="karnott-sdk-button-alternatives-trigger"
              >
                <ChevronIcon size={iconSize} color="currentColor" />
              </AlternativesTrigger>
              <ActionMenuPopover
                triggerRef={alternativesTriggerRef}
                items={alternatives}
                opened={alternativesOpened}
                open={() => setAlternativesOpened(true)}
                close={() => setAlternativesOpened(false)}
                position="bottom end"
                offset={spacing('xSmall')}
              />
            </>
          ) : null}
        </WithAlternativesWrapper>
      }
    />
  );
};
